import { useMemo, useState } from 'react';
import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  useDailyInstallations, useDailyInstallationJobs, useDailyInstallationFilters,
} from '../hooks/useDailyInstallations';
import SearchableSelect from '../components/SearchableSelect';
import Modal from '../components/Modal';

// Daily Installations — the NEO2 rebuild of the Power BI page of the same name.
//
// The layout follows the original rather than reinventing it: the report's own
// definition was exported, so the pipeline row, the emphasised Completed card,
// the grouped service-call panel and the table/chart arrangement are all where
// Power BI puts them. The four small shapes between the top cards in the
// original are arrows — that row is a funnel, not five unrelated tiles, and
// rebuilding it as a plain grid would lose the point of the page.
//
// Palette: Nano brand purple on white.
//
// Two modes:
//   Comparable  mirrored SharePoint fields only, counted exactly as the DAX
//               does, so the two can be put side by side and proved equal
//   Full        adds what NEO2 holds and Power BI cannot see

const BRAND = '#711C91';
const num = (v) => Number(v || 0).toLocaleString('en-GB');
const pct = (v) => `${(Number(v || 0) * 100).toFixed(0)}%`;
const today = () => new Date().toISOString().slice(0, 10);

// A pipeline stage. `hero` is the Completed card, which the original draws
// larger than the rest.
// Not started / En route / In progress are clickable (01/10/2026), like the
// Discrepancies card: they narrow the jobs table to that stage.
function Stage({ label, value, hero, tone, onClick, active }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button', onClick, title: active ? 'Click to show all jobs again' : `Click to show only ${label.toLowerCase()} jobs in the table` } : {})}
      className={`flex min-w-0 flex-1 flex-col items-center justify-center rounded-lg border bg-white px-3 text-center ${
        hero ? 'border-brand py-5 shadow-sm' : active ? 'border-amber-500 py-4 ring-1 ring-amber-500' : `border-gray-200 py-4${onClick ? ' transition hover:border-amber-400' : ''}`}`}
    >
      <span
        className={`tabular-nums font-bold leading-none ${hero ? 'text-4xl' : 'text-3xl'}`}
        style={{ color: tone || (hero ? BRAND : '#252423') }}
      >
        {value}
      </span>
      <span className="mt-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
    </Tag>
  );
}

function Arrow() {
  return (
    <ChevronRightIcon className="hidden h-5 w-5 shrink-0 self-center text-gray-300 sm:block" aria-hidden="true" />
  );
}

function MiniCard({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
      <p className="text-2xl font-bold tabular-nums leading-none" style={{ color: tone || '#252423' }}>{value}</p>
      <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
    </div>
  );
}

function RegionBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.jobs || 0)));
  return (
    <div className="space-y-1.5">
      {rows.slice(0, 12).map((r) => (
        <div key={r.region} className="flex items-center gap-2">
          <span className="w-28 shrink-0 truncate text-xs text-gray-600" title={r.region}>{r.region}</span>
          <div className="h-3.5 flex-1 overflow-hidden rounded-sm bg-gray-100">
            <div className="h-full" style={{ width: `${(Number(r.completed) / max) * 100}%`, background: BRAND }} />
          </div>
          <span className="w-16 shrink-0 text-right text-xs tabular-nums text-gray-500">
            {num(r.completed)}/{num(r.jobs)}
          </span>
        </div>
      ))}
    </div>
  );
}

function DailyInstallations() {
  const [mode, setMode] = useState('sharepoint');
  const [dateMode, setDateMode] = useState('day'); // 'day' = single day, 'range' = from/to
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [region, setRegion] = useState('');
  const [contract, setContract] = useState('');
  const [engineer, setEngineer] = useState('');
  const [address, setAddress] = useState('');
  // When on, the jobs table shows only booking-vs-completion discrepancies.
  const [discrepancyOnly, setDiscrepancyOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(''); // narrows the jobs table only
  const [stageFilter, setStageFilter] = useState(''); // clicked pipeline stage, jobs table only
  const toggleStage = (st) => setStageFilter((cur) => (cur === st ? '' : st));
  const [noteJob, setNoteJob] = useState(null); // job whose full note is open

  const filters = useMemo(() => {
    const base = { region, contract, engineer, address: address.trim() || null };
    return dateMode === 'range' ? { ...base, from, to } : { ...base, date };
  }, [dateMode, date, from, to, region, contract, engineer, address]);
  // The summary uses the plain filters (so the KPI/discrepancy counts stay whole);
  // status and the discrepancy toggle narrow only the jobs table.
  const jobsFilters = useMemo(() => {
    const f = { ...filters };
    if (statusFilter) f.status = statusFilter;
    if (discrepancyOnly) f.discrepancy = 1;
    if (stageFilter) f.stage = stageFilter;
    return f;
  }, [filters, statusFilter, discrepancyOnly, stageFilter]);

  const { data, isLoading, isFetching, refetch, isError } = useDailyInstallations(filters, mode);
  const jobsQ = useDailyInstallationJobs(jobsFilters, mode, { limit: 200 });
  const { data: opts } = useDailyInstallationFilters();

  const s = data?.summary || {};
  const full = mode === 'full';
  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      {/* ---- header + mode ---- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Daily Installations</h1>
          <p className="text-sm text-gray-500">Installation activity by day, region and engineer.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-gray-300">
            <button
              type="button" onClick={() => setMode('sharepoint')}
              className={`px-3 py-1.5 text-xs font-medium ${!full ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              style={!full ? { background: BRAND } : undefined}
            >
              Comparable
            </button>
            <button
              type="button" onClick={() => setMode('full')}
              className={`px-3 py-1.5 text-xs font-medium ${full ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              style={full ? { background: BRAND } : undefined}
            >
              Full platform data
            </button>
          </div>
          <button
            type="button" onClick={() => refetch()}
            className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:border-brand"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-400">
        {full
          ? 'Platform data: SharePoint job records plus the stock each job consumed and faulty returns — figures the Power BI report cannot produce.'
          : 'Counted exactly as the Power BI report counts, from mirrored SharePoint fields only, so the two can be compared directly.'}
      </p>

      {/* ---- filter bar, as the original runs it across the top ---- */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-4">
        <div className="flex overflow-hidden rounded-md border border-gray-300">
          {[['day', 'Daily'], ['range', 'Range']].map(([m, label]) => (
            <button
              key={m} type="button" onClick={() => setDateMode(m)}
              className={`px-3 py-1.5 text-sm font-medium ${dateMode === m ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              style={dateMode === m ? { background: BRAND } : undefined}
            >{label}</button>
          ))}
        </div>
        {dateMode === 'day' ? (
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={sel} />
        ) : (
          <>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={sel} title="From" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={sel} title="To" />
          </>
        )}
        {/* Region B / Region N (stored as BRSK / Netomnia). The next select is
            the finer area, which the jobs call "region". */}
        <select value={contract} onChange={(e) => setContract(e.target.value)} className={sel} aria-label="Region">
          <option value="">Both regions</option>
          <option value="BRSK">Region B</option>
          <option value="NETOMNIA">Region N</option>
        </select>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={sel} aria-label="Area">
          <option value="">All areas</option>
          {(opts?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {/* Narrows the jobs table only (the KPIs above stay whole). Options are
            the statuses actually present in the current window. */}
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={sel} title="Filter the table by status">
          <option value="">All statuses</option>
          {(data?.by_status || []).map((b) => <option key={b.status} value={b.status}>{b.status} ({b.jobs})</option>)}
        </select>
        {/* The two searches sit together at the end of the bar. Searchable
            rather than a plain select: the engineer list runs to hundreds of
            names, and scrolling one is not a way to find a person. */}
        <SearchableSelect
          id="daily-installations-engineer"
          value={engineer}
          onChange={setEngineer}
          options={opts?.engineers || []}
          allLabel="All engineers"
          placeholder="Type an engineer's name…"
          className="w-56"
        />
        <input type="search" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Search address…" className={`${sel} w-56`} />
      </div>

      {isError && <p className="mt-4 text-sm text-red-500">Could not load the figures.</p>}
      {isLoading && <p className="mt-4 text-sm text-gray-400">Loading…</p>}

      {data && (
        <>
          {/* ---- the pipeline. Arrows because the original draws arrows. ---- */}
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <Stage label="Jobs booked" value={num(s.jobs_booked)} />
            <Arrow />
            <Stage label="Not started" value={num(s.installations_not_started)} onClick={() => toggleStage('not_started')} active={stageFilter === 'not_started'} />
            <Arrow />
            <Stage label="En route" value={num(s.installations_en_route)} onClick={() => toggleStage('en_route')} active={stageFilter === 'en_route'} />
            <Arrow />
            <Stage label="In progress" value={num(s.installations_in_progress)} onClick={() => toggleStage('in_progress')} active={stageFilter === 'in_progress'} />
            <Arrow />
            <Stage label="Completed" value={num(s.installations_completed)} hero />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_300px]">
            {/* self-start: this column sits beside the taller service-calls
                panel, and without it the grid stretches the cards to match,
                leaving each one mostly empty. */}
            <div className="grid grid-cols-2 gap-3 self-start sm:grid-cols-5">
              <MiniCard label="Installs booked" value={num(s.installs_booked)} />
              <MiniCard label="Completion rate" value={pct(s.completion_rate)} tone={BRAND} />
              <MiniCard label="UTI" value={num(s.uti_count)} tone={s.uti_count ? '#D64550' : undefined} />
              <MiniCard label="DNA" value={num(s.dna_count)} tone={s.dna_count ? '#E66C37' : undefined} />
              <button
                type="button"
                onClick={() => setDiscrepancyOnly((v) => !v)}
                className={`rounded-lg border bg-white p-3 text-center transition ${discrepancyOnly ? 'border-amber-500 ring-1 ring-amber-500' : 'border-gray-200 hover:border-amber-400'}`}
                title="Booking type vs completed status mismatch — click to show only these in the table"
              >
                <p className="text-2xl font-bold tabular-nums leading-none" style={{ color: s.discrepancies ? '#B45309' : '#252423' }}>{num(s.discrepancies)}</p>
                <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Discrepancies</p>
              </button>
            </div>

            {/* Service calls grouped in their own panel, as the original does. */}
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Service calls</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-white p-2 text-center">
                  <p className="text-xl font-bold tabular-nums leading-none text-gray-900">{num(s.service_calls_booked)}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-gray-500">Booked</p>
                </div>
                <div className="rounded-md bg-white p-2 text-center">
                  <p className="text-xl font-bold tabular-nums leading-none" style={{ color: BRAND }}>{num(s.service_calls_completed)}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-gray-500">Completed</p>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Replacement jobs are not shown — that field isn’t mirrored from SharePoint.
              </p>
            </div>
          </div>

          {/* ---- table left, chart right, as the original arranges them ---- */}
          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_300px]">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="max-h-[26rem] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Engineer</th>
                      <th className="px-3 py-2">Slot</th>
                      <th className="px-3 py-2">Address</th>
                      <th className="px-3 py-2">Externals</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Notes</th>
                      {full ? <th className="px-3 py-2 text-right">Stock</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(jobsQ.data?.rows || []).map((j) => (
                      <tr key={j.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-xs text-gray-700">{j.engineer_name || '—'}</td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{j.time_slot || '—'}</td>
                        <td className="px-3 py-1.5 text-xs text-gray-800">{j.address || '—'}</td>
                        <td className="px-3 py-1.5 text-xs">
                          {j.externals_status
                            ? <span className={/complete/i.test(j.externals_status) ? 'font-medium text-green-600' : 'text-gray-600'}>{j.externals_status}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-700">
                          {j.discrepancy ? <span className="mr-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700" title="Booking type and completed status disagree">⚠</span> : null}
                          {j.install_status || '—'}
                        </td>
                        <td className="max-w-[16rem] px-3 py-1.5 text-xs text-gray-600">
                          {j.engineer_notes && j.engineer_notes.trim() && j.engineer_notes.trim() !== '.'
                            ? (
                              <button
                                type="button"
                                onClick={() => setNoteJob(j)}
                                className="block max-w-full truncate text-left text-brand hover:underline"
                                title="Click to read the full note"
                              >
                                {j.engineer_notes}
                              </button>
                            )
                            : <span className="text-gray-300">—</span>}
                        </td>
                        {full ? (
                          <td className="px-3 py-1.5 text-right text-xs tabular-nums text-gray-600">
                            {j.stock_lines ? num(j.stock_units) : <span className="text-gray-300">—</span>}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                    {!jobsQ.isLoading && !(jobsQ.data?.rows || []).length ? (
                      <tr><td colSpan={full ? 7 : 6} className="px-3 py-10 text-center text-gray-400">No jobs for this day.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <p className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-400">
                Engineer notes are shown; click a note to read it in full. Customer notes aren’t mirrored from SharePoint.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Completed by region
              </p>
              {(data.by_region || []).length
                ? <RegionBars rows={data.by_region} />
                : <p className="text-sm text-gray-400">Nothing for this day.</p>}
            </div>
          </div>

          {/* ---- status breakdown, the original's list slicer ---- */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">By status</p>
            <div className="flex flex-wrap gap-1.5">
              {(data.by_status || []).map((b) => (
                <span key={b.status} className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700">
                  {b.status}
                  <span className="ml-1.5 font-semibold tabular-nums" style={{ color: BRAND }}>{num(b.jobs)}</span>
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {noteJob ? (
        <Modal
          title="Engineer note"
          subtitle={[noteJob.engineer_name, noteJob.address, noteJob.install_status].filter(Boolean).join(' · ')}
          onClose={() => setNoteJob(null)}
          width="max-w-xl"
        >
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{noteJob.engineer_notes}</p>
        </Modal>
      ) : null}
    </div>
  );
}

export default DailyInstallations;
